import { 
  ProjectionChart, 
  ProjectionEntity, 
  TimeFrame, 
  VelocityMetrics, 
  ProjectionDataPoint,
  ProjectionSummary,
  CompletionPrediction,
  BurndownData
} from '../types/projections';
import { aspirationsService } from './aspirationsService';
import { goalsService } from './goalsService';
import { listsService } from './listsService';

export class ProjectionsService {
  // Calculate completion velocity based on historical data
  calculateVelocity(entity: ProjectionEntity, days: number = 30): VelocityMetrics {
    const now = new Date();
    const startDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    
    // Get completed items in the time period
    let completedCount = 0;
    
    if (entity === 'lists') {
      const lists = listsService.getAllLists();
      completedCount = lists.filter(list => 
        list.status === 'complete' && 
        list.completedDate && 
        new Date(list.completedDate) >= startDate
      ).length;
    } else if (entity === 'goals') {
      const goals = goalsService.getAllGoals();
      completedCount = goals.filter(goal => 
        goal.status === 'completed' && 
        goal.completedDate && 
        new Date(goal.completedDate) >= startDate
      ).length;
    } else if (entity === 'aspirations') {
      const aspirations = aspirationsService.getAspirationsSync();
      completedCount = aspirations.filter(asp => 
        asp.status === 'completed' && 
        asp.updatedAt >= startDate
      ).length;
    }
    
    const dailyAverage = completedCount / days;
    const weeklyAverage = dailyAverage * 7;
    const monthlyAverage = dailyAverage * 30;
    
    // Calculate trend (compare last week to previous week)
    const lastWeekCount = this.getCompletedCountInRange(entity, 7);
    const previousWeekCount = this.getCompletedCountInRange(entity, 14, 7);
    
    let trend: 'increasing' | 'stable' | 'decreasing' = 'stable';
    let trendPercentage = 0;
    
    if (previousWeekCount > 0) {
      trendPercentage = ((lastWeekCount - previousWeekCount) / previousWeekCount) * 100;
      if (trendPercentage > 10) trend = 'increasing';
      else if (trendPercentage < -10) trend = 'decreasing';
    }
    
    return {
      dailyAverage: Math.round(dailyAverage * 10) / 10,
      weeklyAverage: Math.round(weeklyAverage * 10) / 10,
      monthlyAverage: Math.round(monthlyAverage * 10) / 10,
      trend,
      trendPercentage: Math.round(trendPercentage)
    };
  }
  
  private getCompletedCountInRange(entity: ProjectionEntity, daysAgo: number, daysFrom: number = 0): number {
    const now = new Date();
    const startDate = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);
    const endDate = new Date(now.getTime() - daysFrom * 24 * 60 * 60 * 1000);
    
    if (entity === 'lists') {
      const lists = listsService.getAllLists();
      return lists.filter(list => 
        list.status === 'complete' && 
        list.completedDate && 
        new Date(list.completedDate) >= startDate &&
        new Date(list.completedDate) < endDate
      ).length;
    } else if (entity === 'goals') {
      const goals = goalsService.getAllGoals();
      return goals.filter(goal => 
        goal.status === 'completed' && 
        goal.completedDate && 
        new Date(goal.completedDate) >= startDate &&
        new Date(goal.completedDate) < endDate
      ).length;
    } else {
      const aspirations = aspirationsService.getAspirationsSync();
      return aspirations.filter(asp => 
        asp.status === 'completed' && 
        asp.updatedAt >= startDate &&
        asp.updatedAt < endDate
      ).length;
    }
  }

  // Project completion dates based on current velocity
  projectCompletionDates(entity: ProjectionEntity): CompletionPrediction[] {
    const velocity = this.calculateVelocity(entity);
    const predictions: CompletionPrediction[] = [];
    
    // Get incomplete items
    let incompleteItems: any[] = [];
    
    if (entity === 'lists') {
      incompleteItems = listsService.getAllLists()
        .filter(list => list.status !== 'complete');
    } else if (entity === 'goals') {
      incompleteItems = goalsService.getAllGoals()
        .filter(goal => goal.status !== 'completed' && goal.status !== 'cancelled');
    } else {
      incompleteItems = aspirationsService.getAspirationsSync()
        .filter(asp => asp.status !== 'completed');
    }
    
    // Sort by priority
    incompleteItems.sort((a, b) => a.priority - b.priority);
    
    // Project completion dates
    const dailyVelocity = Math.max(velocity.dailyAverage, 0.1); // Avoid division by zero
    let daysAccumulated = 0;
    
    incompleteItems.forEach(item => {
      daysAccumulated += 1 / dailyVelocity;
      const projectedDate = new Date();
      projectedDate.setDate(projectedDate.getDate() + Math.round(daysAccumulated));
      
      // Calculate confidence based on velocity trend and consistency
      let confidenceLevel = 50; // Base confidence
      if (velocity.trend === 'increasing') confidenceLevel += 20;
      else if (velocity.trend === 'decreasing') confidenceLevel -= 20;
      
      // Adjust based on how far in the future
      if (daysAccumulated > 90) confidenceLevel -= 20;
      else if (daysAccumulated < 30) confidenceLevel += 20;
      
      confidenceLevel = Math.max(0, Math.min(100, confidenceLevel));
      
      predictions.push({
        itemId: item.id,
        itemTitle: item.title,
        currentProgress: item.progress || 0,
        projectedDate,
        confidenceLevel,
        basedOnVelocity: velocity
      });
    });
    
    return predictions;
  }

  // Generate chart data for visualization
  generateChartData(entity: ProjectionEntity, timeframe: TimeFrame): ProjectionChart {
    const dataPoints: ProjectionDataPoint[] = [];
    const now = new Date();
    let startDate = new Date();
    let endDate = new Date();
    
    // Set date range based on timeframe
    switch (timeframe) {
      case 'week':
        startDate.setDate(now.getDate() - 7);
        endDate.setDate(now.getDate() + 7);
        break;
      case 'month':
        startDate.setMonth(now.getMonth() - 1);
        endDate.setMonth(now.getMonth() + 1);
        break;
      case 'quarter':
        startDate.setMonth(now.getMonth() - 3);
        endDate.setMonth(now.getMonth() + 3);
        break;
      case 'year':
        startDate.setFullYear(now.getFullYear() - 1);
        endDate.setFullYear(now.getFullYear() + 1);
        break;
      default:
        startDate.setFullYear(now.getFullYear() - 2);
        endDate.setFullYear(now.getFullYear() + 2);
    }
    
    // Get historical data
    const historicalData = this.getHistoricalData(entity, startDate, now);
    const projectedData = this.getProjectedData(entity, now, endDate);
    
    // Combine data
    dataPoints.push(...historicalData, ...projectedData);
    
    // Calculate summary
    const summary = this.calculateSummary(entity);
    
    return {
      entity,
      timeframe,
      dataPoints,
      summary
    };
  }
  
  private getHistoricalData(entity: ProjectionEntity, startDate: Date, endDate: Date): ProjectionDataPoint[] {
    const dataPoints: ProjectionDataPoint[] = [];
    let items: any[] = [];
    
    if (entity === 'lists') {
      items = listsService.getAllLists();
    } else if (entity === 'goals') {
      items = goalsService.getAllGoals();
    } else {
      items = aspirationsService.getAspirationsSync();
    }
    
    // Group by date
    const dateMap = new Map<string, number>();
    
    items.forEach(item => {
      const completedDate = item.completedDate || item.updatedAt;
      if (completedDate && new Date(completedDate) >= startDate && new Date(completedDate) <= endDate) {
        const dateKey = new Date(completedDate).toISOString().split('T')[0];
        dateMap.set(dateKey, (dateMap.get(dateKey) || 0) + 1);
      }
    });
    
    // Convert to data points
    dateMap.forEach((count, date) => {
      dataPoints.push({
        date,
        value: count,
        label: `Completed: ${count}`,
        entity: entity,
        entityId: '',
        type: 'actual'
      });
    });
    
    return dataPoints;
  }
  
  private getProjectedData(entity: ProjectionEntity, startDate: Date, endDate: Date): ProjectionDataPoint[] {
    const dataPoints: ProjectionDataPoint[] = [];
    const predictions = this.projectCompletionDates(entity);
    
    // Group predictions by date
    const dateMap = new Map<string, number>();
    
    predictions.forEach(prediction => {
      if (prediction.projectedDate >= startDate && prediction.projectedDate <= endDate) {
        const dateKey = prediction.projectedDate.toISOString().split('T')[0];
        dateMap.set(dateKey, (dateMap.get(dateKey) || 0) + 1);
      }
    });
    
    // Convert to data points
    dateMap.forEach((count, date) => {
      dataPoints.push({
        date,
        value: count,
        label: `Projected: ${count}`,
        entity: entity,
        entityId: '',
        type: 'projected'
      });
    });
    
    return dataPoints;
  }
  
  private calculateSummary(entity: ProjectionEntity): ProjectionSummary {
    let totalItems = 0;
    let completedItems = 0;
    let inProgressItems = 0;
    let notStartedItems = 0;
    
    if (entity === 'lists') {
      const lists = listsService.getAllLists();
      totalItems = lists.length;
      completedItems = lists.filter(l => l.status === 'complete').length;
      inProgressItems = lists.filter(l => l.status === 'in_progress').length;
      notStartedItems = lists.filter(l => l.status === 'ready').length;
    } else if (entity === 'goals') {
      const goals = goalsService.getAllGoals();
      totalItems = goals.length;
      completedItems = goals.filter(g => g.status === 'completed').length;
      inProgressItems = goals.filter(g => g.status === 'in_progress').length;
      notStartedItems = goals.filter(g => g.status === 'not_started').length;
    } else {
      const aspirations = aspirationsService.getAspirationsSync();
      totalItems = aspirations.length;
      completedItems = aspirations.filter(a => a.status === 'completed').length;
      inProgressItems = aspirations.filter(a => a.status === 'in_progress').length;
      notStartedItems = aspirations.filter(a => a.status === 'not_started').length;
    }
    
    const velocity = this.calculateVelocity(entity);
    const remainingItems = totalItems - completedItems;
    const daysToComplete = velocity.dailyAverage > 0 ? remainingItems / velocity.dailyAverage : null;
    const projectedCompletionDate = daysToComplete ? new Date(Date.now() + daysToComplete * 24 * 60 * 60 * 1000) : null;
    
    let confidenceLevel: 'high' | 'medium' | 'low' = 'medium';
    if (velocity.trend === 'increasing' && velocity.dailyAverage > 0.5) confidenceLevel = 'high';
    else if (velocity.trend === 'decreasing' || velocity.dailyAverage < 0.1) confidenceLevel = 'low';
    
    return {
      totalItems,
      completedItems,
      inProgressItems,
      notStartedItems,
      averageCompletionTime: daysToComplete || 0,
      projectedCompletionDate,
      confidenceLevel,
      completionPercentage: totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0
    };
  }

  // Get burn-down chart data
  getBurndownData(entity: ProjectionEntity, days: number = 30): BurndownData[] {
    const data: BurndownData[] = [];
    const now = new Date();
    const startDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    
    // Get total items at start
    let totalItems = 0;
    if (entity === 'lists') {
      totalItems = listsService.getAllLists().length;
    } else if (entity === 'goals') {
      totalItems = goalsService.getAllGoals().length;
    } else {
      totalItems = aspirationsService.getAspirationsSync().length;
    }
    
    // Calculate ideal burn-down (linear)
    const idealRate = totalItems / days;
    
    // Calculate actual and projected for each day
    for (let i = 0; i <= days * 2; i++) {
      const currentDate = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
      const dateStr = currentDate.toISOString().split('T')[0];
      
      // Ideal line
      const ideal = Math.max(0, totalItems - (idealRate * i));
      
      // Actual (for past dates)
      let actual = totalItems;
      if (currentDate <= now) {
        const completedByDate = this.getCompletedCountByDate(entity, currentDate);
        actual = totalItems - completedByDate;
      }
      
      // Projected (for future dates)
      let projected = actual;
      if (currentDate > now) {
        const velocity = this.calculateVelocity(entity);
        const daysSinceNow = Math.floor((currentDate.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
        const projectedCompleted = velocity.dailyAverage * daysSinceNow;
        projected = Math.max(0, actual - projectedCompleted);
      }
      
      data.push({
        date: dateStr,
        ideal: Math.round(ideal),
        actual: Math.round(actual),
        projected: Math.round(projected)
      });
    }
    
    return data;
  }
  
  private getCompletedCountByDate(entity: ProjectionEntity, date: Date): number {
    let count = 0;
    
    if (entity === 'lists') {
      count = listsService.getAllLists().filter(list => 
        list.status === 'complete' && 
        list.completedDate && 
        new Date(list.completedDate) <= date
      ).length;
    } else if (entity === 'goals') {
      count = goalsService.getAllGoals().filter(goal => 
        goal.status === 'completed' && 
        goal.completedDate && 
        new Date(goal.completedDate) <= date
      ).length;
    } else {
      count = aspirationsService.getAspirationsSync().filter(asp => 
        asp.status === 'completed' && 
        asp.updatedAt <= date
      ).length;
    }
    
    return count;
  }
}

export const projectionsService = new ProjectionsService();